import React, { useState, useMemo } from "react";
import {
  Box, Text, VStack, HStack, Button, Badge,
  useColorModeValue, Icon, Flex, Progress, Divider, Collapse, Tooltip,
} from "@chakra-ui/react";
import {
  FaClipboardList, FaPlay, FaChartBar, FaTrophy,
  FaCheckCircle, FaClock, FaChevronDown, FaChevronUp, FaLayerGroup,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useStudentContext } from "../B2CStudentContext";

/* ── Shared helper -- null/undefined = never attempted, 0 = real zero score ── */
const hasAttempted = (qs: any) =>
  qs.last_score_percentage !== null && qs.last_score_percentage !== undefined;

const getColor = (index: number) => {
  const colors = ["blue", "purple", "teal", "orange", "pink", "cyan"];
  return colors[index % colors.length];
};

/* ========================================================= */
/* QUESTION SET CARD                                          */
/* ========================================================= */

const QuestionSetCard = ({
  questionSet,
  onTimedAttempt,
  onUntimedAttempt,
  onPastAttempts,
  colorScheme,
}: any) => {
  const cardBg = useColorModeValue("blue.50", "blue.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const inProgressBg = useColorModeValue("orange.50", "orange.900");
  const inProgressBorder = useColorModeValue("orange.200", "orange.700");

  const attempted = hasAttempted(questionSet);
  const score = questionSet.last_score_percentage ?? 0;
  const inProgress = questionSet.is_attempt_in_progress ?? false;
  const scoreColor = score >= 80 ? "green" : score >= 50 ? "orange" : "red";

  return (
    <Box
      bg={inProgress ? inProgressBg : cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={inProgress ? inProgressBorder : "transparent"}
      p={4}
      minW="260px"
      flex="0 0 auto"
      position="relative"
    >
      {inProgress && (
        <Badge position="absolute" top={2} right={2}
          colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
          In Progress
        </Badge>
      )}
      <VStack align="stretch" spacing={3}>
        <Text fontWeight="bold" fontSize="sm" pr={inProgress ? 16 : 0}>
          {questionSet.title}
        </Text>
        {attempted ? (
          <Box>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs" color={mutedText}>Last Score</Text>
              <Text fontSize="xs" fontWeight="bold" color={`${scoreColor}.500`}>
                {score}%
              </Text>
            </HStack>
            <Tooltip label={`Last score: ${score}%`}>
              <Progress value={score} size="sm" colorScheme={scoreColor} borderRadius="full" />
            </Tooltip>
          </Box>
        ) : (
          <Box>
            <Text fontSize="xs" color={mutedText} mb={1}>Not attempted yet</Text>
            <Progress value={0} size="sm" colorScheme={colorScheme} borderRadius="full" />
          </Box>
        )}
        <HStack spacing={2}>
          <Button size="xs" 
         //colorScheme={colorScheme} 
          colorScheme="blue"
          leftIcon={<FaPlay />} onClick={onTimedAttempt}>
            Timed
          </Button>
          <Button size="xs" variant="solid" colorScheme="teal" onClick={onUntimedAttempt}>
            Untimed
          </Button>
          <Button size="xs" variant="solid" leftIcon={<FaChartBar />} onClick={onPastAttempts}>
            Progress
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

/* ========================================================= */
/* GROUP SWIMLANE                                              */
/* ========================================================= */

const GroupSwimlane = ({
  group,
  index,
  isDefaultOpen,
  navigate,
  basePath,
  onStartAttempt,
}: any) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const colorScheme = getColor(index);
  const headerBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const headerHoverBg = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`);
  const mutedText = useColorModeValue("gray.500", "gray.400");

  const totalSets = group.question_sets.length;
  const attemptedQsets = group.question_sets.filter(hasAttempted);
  const attempted = attemptedQsets.length;
  const avgScore = attempted > 0
    ? Math.round(
        attemptedQsets.reduce((sum: number, qs: any) => sum + (qs.last_score_percentage ?? 0), 0) /
        attempted
      )
    : null;
  const inProgressCount = group.question_sets.filter(
    (qs: any) => qs.is_attempt_in_progress
  ).length;
  const groupProgress = totalSets > 0
    ? Math.round((attempted / totalSets) * 100)
    : 0;

  return (
    <Box mb={4} borderRadius="xl" overflow="hidden"
      borderWidth="1px"
      borderColor={useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`)}>

      <Box
        bg={headerBg}
        px={4}
        py={3}
        borderLeft="5px solid"
        borderColor={`${colorScheme}.400`}
        cursor="pointer"
        _hover={{ bg: headerHoverBg }}
        onClick={() => setIsOpen(!isOpen)}
        transition="background 0.15s"
      >
        <Flex align="center">
          <HStack spacing={3} flex={1}>
            <Icon as={FaLayerGroup} color={`${colorScheme}.500`} />
            <Text fontWeight="bold" fontSize="md">{group.title}</Text>
            {inProgressCount > 0 && (
              <Badge colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
                {inProgressCount} in progress
              </Badge>
            )}
          </HStack>

          <HStack spacing={4} mr={4} display={{ base: "none", md: "flex" }}>
            <HStack spacing={1}>
              <Icon as={FaCheckCircle} color="green.400" boxSize={3} />
              <Text fontSize="xs" color={mutedText}>
                {attempted}/{totalSets} done
              </Text>
            </HStack>
            {avgScore !== null && (
              <HStack spacing={1}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={3} />
                <Text fontSize="xs" color={mutedText}>
                  Avg {avgScore}%
                </Text>
              </HStack>
            )}
            <Box w="80px">
              <Progress value={groupProgress} size="xs"
                colorScheme={colorScheme} borderRadius="full" />
            </Box>
          </HStack>

          <Icon
            as={isOpen ? FaChevronUp : FaChevronDown}
            color={`${colorScheme}.500`}
            boxSize={4}
          />
        </Flex>
      </Box>

      <Collapse in={isOpen} animateOpacity>
        <Box px={4} py={4} bg={useColorModeValue("white", "gray.800")}>
          <HStack spacing={4} overflowX="auto" pb={2} align="stretch">
            {group.question_sets
              .sort((a: any, b: any) => a.sequence_number - b.sequence_number)
              .map((qs: any) => (
                <QuestionSetCard
                  key={qs.question_set_id}
                  questionSet={qs}
                  colorScheme={colorScheme}
                  onTimedAttempt={() => onStartAttempt(qs.question_set_id, true)}
                  onUntimedAttempt={() => onStartAttempt(qs.question_set_id, false)}
                  onPastAttempts={() =>
                    navigate(`${basePath}/question-set/${qs.question_set_id}/attempts`)
                  }
                />
              ))}
          </HStack>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ========================================================= */
/* MAIN PAGE                                                 */
/* ========================================================= */

const B2CSectionPractice = () => {
  const navigate = useNavigate();
  const { entitlement_id, practice_set_id, practice_set_section_id } = useParams();
  const { studentContext } = useStudentContext();

  const cardBg = useColorModeValue("teal.50", "teal.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const statBg = useColorModeValue("gray.50", "gray.750");

  const entitlement = useMemo(() => {
    return studentContext?.entitlements?.find(
      (e) => e.entitlement_id === Number(entitlement_id)
    );
  }, [studentContext, entitlement_id]);

  const practiceSet = entitlement?.practice_set;

  const section = useMemo(() => {
    return practiceSet?.sections?.find(
      (s) => s.practice_set_section_id === Number(practice_set_section_id)
    );
  }, [practiceSet, practice_set_section_id]);

  if (!practiceSet || !section) {
    return <Box p={10}><Text>Section not found.</Text></Box>;
  }

  const handleStartAttempt = async (
    question_set_id: number,
    timerEnabled: boolean
  ) => {
    const params = new URLSearchParams({
      entitlement_id: String(entitlement?.entitlement_id),
      practice_set_id: String(practice_set_id),
      assessment_type: "QUESTION_SET",
      timer_enabled: String(timerEnabled),
      question_set_id: String(question_set_id),
    });
    navigate(`/practice/app/practice-attempt/attempt-instructions?${params.toString()}`);
  };

  // ── Section-level analytics -- fixed to use hasAttempted, not score > 0 ──
  const allQsets = section.groups.flatMap(g => g.question_sets);
  const totalSets = allQsets.length;
  const attemptedSets = allQsets.filter(hasAttempted);
  const completedCount = attemptedSets.length;
  const overallProgress = totalSets > 0
    ? Math.round((completedCount / totalSets) * 100)
    : 0;
  const avgScore = completedCount > 0
    ? Math.round(
        attemptedSets.reduce((sum, qs) => sum + (qs.last_score_percentage ?? 0), 0) /
        completedCount
      )
    : null;
  const inProgressCount = allQsets.filter(
    qs => qs.is_attempt_in_progress
  ).length;

  const progressColor =
    overallProgress === 100 ? "green" :
    overallProgress > 50 ? "blue" :
    overallProgress > 0 ? "orange" : "gray";

  const sortedGroups = [...section.groups].sort(
    (a, b) => a.sequence_number - b.sequence_number
  );

  return (
    <Box maxW="1400px" mx="auto" p={6}>

      <Box bg={cardBg} borderRadius="2xl" borderWidth="1px"
        borderColor={borderColor} p={6} mb={6}>
        <Flex align="start" gap={6} direction={{ base: "column", md: "row" }}>

          <VStack align="start" spacing={3} flex={1}>
            <HStack spacing={2}>
              <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                Section
              </Badge>
              {inProgressCount > 0 && (
                <Badge colorScheme="orange" px={3} py={1} borderRadius="full">
                  {inProgressCount} in progress
                </Badge>
              )}
            </HStack>

            <Text fontSize="2xl" fontWeight="bold">{section.title}</Text>

            <Text color={mutedText} fontSize="sm">
              {section.groups.length} groups · {totalSets} question sets
            </Text>

            <Box w="100%">
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color={mutedText}>Section Progress</Text>
                <Text fontSize="xs" fontWeight="semibold"
                  color={`${progressColor}.500`}>
                  {completedCount}/{totalSets} sets attempted
                </Text>
              </Flex>
              <Progress value={overallProgress} size="sm"
                colorScheme={progressColor} borderRadius="full" />
            </Box>

            <HStack spacing={3} pt={1}>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<FaChartBar />}
                onClick={() =>
                  navigate(
                    `/practice/app/entitlement/${entitlement_id}/practice-set/${practice_set_id}/attempts`
                  )
                }
              >
                My Progress
              </Button>
            </HStack>
          </VStack>

          <HStack spacing={4} flexShrink={0} align="stretch"
            direction={{ base: "row", md: "row" }}>
            {[
              {
                label: "Completed",
                value: `${completedCount}/${totalSets}`,
                icon: FaCheckCircle,
                color: "green",
              },
              {
                label: "Avg Score",
                value: avgScore !== null ? `${avgScore}%` : "—",
                icon: FaTrophy,
                color: "yellow",
              },
              {
                label: "Progress",
                value: `${overallProgress}%`,
                icon: FaClock,
                color: progressColor,
              },
            ].map((stat) => (
              <Box key={stat.label} bg={statBg} borderRadius="xl"
                borderWidth="1px" borderColor={borderColor}
                p={4} textAlign="center" minW="90px">
                <Icon as={stat.icon} color={`${stat.color}.500`}
                  boxSize={4} mb={1} />
                <Text fontSize="xl" fontWeight="bold">{stat.value}</Text>
                <Text fontSize="xs" color={mutedText}>{stat.label}</Text>
              </Box>
            ))}
          </HStack>
        </Flex>
      </Box>

      <Divider mb={6} />

      <VStack align="stretch" spacing={0}>
        {sortedGroups.map((group, idx) => (
          <GroupSwimlane
            key={group.practice_set_group_id}
            group={group}
            index={idx}
            isDefaultOpen={idx === 0}
            navigate={navigate}
            basePath={`/practice/app/entitlement/${entitlement_id}/practice-set/${practice_set_id}/practice/${practice_set_section_id}`}
            onStartAttempt={handleStartAttempt}
          />
        ))}
      </VStack>

    </Box>
  );
};

export default B2CSectionPractice;